/**
 * RemindersPage — "Mis recordatorios": todos los recordatorios próximos del
 * agente (o de todo el equipo si admin), no solo dentro de cada lead.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, isPast, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { Bell, Clock, Trash2, ExternalLink, ChevronRight, CheckCircle2 } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import ZicassoLeadModal from "./ZicassoLeadModal";

const fmt = (iso) => { try { return format(new Date(iso), "EEE d MMM · HH:mm", { locale: es }); } catch { return iso; } };

function ReminderCard({ r, showAgent, onOpen, onDelete, deleting }) {
  const due = r.due_at ? new Date(r.due_at) : null;
  const overdue = r.status === "pending" && due && isPast(due);
  const today = due && isToday(due);
  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-xl border bg-card p-4 shadow-sm transition-shadow hover:shadow-md ${
        overdue ? "border-[#C45A45]/40" : "border-black/5"
      }`}
      data-testid={`my-reminder-${r.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Clock className={`h-4 w-4 ${overdue ? "text-[#C45A45]" : "text-primary"}`} />
          <span className="text-sm font-semibold">{fmt(r.due_at)}</span>
          {overdue && <Badge className="h-4 bg-[#FCEEEA] text-[9px] text-[#803D26] hover:bg-[#FCEEEA]">Vencido</Badge>}
          {today && !overdue && <Badge className="h-4 bg-[#E2EBE5] text-[9px] text-[#244532] hover:bg-[#E2EBE5]">Hoy</Badge>}
          {r.status === "sent" && <Badge variant="outline" className="h-4 text-[9px]">Enviado</Badge>}
        </div>
        <p className="mt-1.5 truncate text-sm font-medium">{r.traveler_name || "Lead"}</p>
        {r.note && <p className="mt-0.5 text-[12px] text-muted-foreground">{r.note}</p>}
        {showAgent && r.agent_name && (
          <p className="mt-1 text-[11px] text-muted-foreground">Agente: {r.agent_name}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="sm" variant="outline" onClick={() => onOpen(r.lead_id)} data-testid={`my-reminder-open-${r.id}`}>
          <ExternalLink className="mr-1 h-3 w-3" /> Abrir
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-[#C45A45]" onClick={() => onDelete(r.id)} disabled={deleting} data-testid={`my-reminder-delete-${r.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function RemindersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [scope, setScope] = useState("mine");
  const [showSent, setShowSent] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["my-reminders", scope],
    queryFn: async () => (await api.get(`/zicasso/reminders?scope=${scope}`)).data,
    refetchInterval: 60_000,
  });

  const delMut = useMutation({
    mutationFn: (rid) => api.delete(`/zicasso/reminders/${rid}`),
    onSuccess: () => { toast.success("Recordatorio eliminado"); qc.invalidateQueries({ queryKey: ["my-reminders"] }); },
    onError: () => toast.error("No se pudo eliminar"),
  });

  const items = data?.items || [];
  const pending = items.filter((r) => r.status === "pending").sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  const sent = items.filter((r) => r.status === "sent").sort((a, b) => new Date(b.due_at) - new Date(a.due_at));
  const showAgent = data?.scope === "all";

  return (
    <div className="mx-auto max-w-[900px] px-6 py-6" data-testid="reminders-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold tracking-tight">
            <Bell className="h-7 w-7 text-primary" /> Mis recordatorios
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Todos tus recordatorios programados sobre los leads.
          </p>
        </div>
        {data?.is_admin && (
          <div className="flex items-center gap-1 rounded-lg border bg-card p-1 text-sm font-medium">
            <button
              onClick={() => setScope("mine")}
              className={`rounded-md px-3 py-1.5 transition-colors ${scope === "mine" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="reminders-scope-mine"
            >
              Míos
            </button>
            <button
              onClick={() => setScope("all")}
              className={`rounded-md px-3 py-1.5 transition-colors ${scope === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="reminders-scope-all"
            >
              Todo el equipo
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
      ) : pending.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-16 text-center" data-testid="reminders-empty">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[#244532]" />
          <p className="font-heading text-lg font-semibold">Sin recordatorios próximos</p>
          <p className="mt-1 text-sm text-muted-foreground">Crea recordatorios desde la pestaña "Recordatorio" de cada lead.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="reminders-pending-list">
          {pending.map((r) => (
            <ReminderCard key={r.id} r={r} showAgent={showAgent} onOpen={setSelectedLead} onDelete={delMut.mutate} deleting={delMut.isPending} />
          ))}
        </div>
      )}

      {sent.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowSent((s) => !s)}
            className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
            data-testid="toggle-sent-reminders"
          >
            <CheckCircle2 className="h-4 w-4" /> Enviados / historial ({sent.length})
            <ChevronRight className={`h-4 w-4 transition-transform ${showSent ? "rotate-90" : ""}`} />
          </button>
          {showSent && (
            <div className="space-y-2 opacity-75" data-testid="reminders-sent-list">
              {sent.map((r) => (
                <ReminderCard key={r.id} r={r} showAgent={showAgent} onOpen={setSelectedLead} onDelete={delMut.mutate} deleting={delMut.isPending} />
              ))}
            </div>
          )}
        </div>
      )}

      <ZicassoLeadModal leadId={selectedLead} open={!!selectedLead} onClose={() => setSelectedLead(null)} initialTab="reminders" />
    </div>
  );
}
